import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faComment,
  faFile,
  faHashtag,
  faIdCard,
  faStethoscope,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { getDisplayLoaNo } from "./transactionLoaDisplayRules";

const isEmptyValue = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const formatDate = (value) => {
  if (isEmptyValue(value) || value === "0000-00-00") return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (isEmptyValue(value) || value === "0000-00-00") return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatCurrency = (value) => {
  if (isEmptyValue(value)) return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(amount);
};

const formatFileSize = (value) => {
  if (isEmptyValue(value)) return "";
  const bytes = Number(value);
  if (Number.isNaN(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const display = (value) => (isEmptyValue(value) ? "-" : String(value));

const normalizeRowsPayload = (payload) => {
  if (Array.isArray(payload)) {
    return {
      rows: payload,
      sourceTable: payload[0]?.source_table || "",
    };
  }

  if (Array.isArray(payload?.rows)) {
    return {
      rows: payload.rows,
      sourceTable: payload.source_table || payload.rows[0]?.source_table || "",
    };
  }

  return {
    rows: [],
    sourceTable: payload?.source_table || "",
  };
};

const getLoaCptPayload = (data) => {
  const candidates = [
    data?.loa_cpt,
    data?.loaCpt,
    data?.loa_lab_procedures,
    data?.loaLabProcedures,
    data?.procedures,
  ];

  for (const candidate of candidates) {
    const payload = normalizeRowsPayload(candidate);
    if (payload.rows.length > 0) return payload;
  }

  return { rows: [], sourceTable: "" };
};

const getLoaIcdPayload = (data) => {
  const candidates = [
    data?.loa_icd,
    data?.loaIcd,
    data?.loa_icds,
    data?.loaIcds,
  ];

  for (const candidate of candidates) {
    const payload = normalizeRowsPayload(candidate);
    if (payload.rows.length > 0) return payload;
  }

  return { rows: [], sourceTable: "" };
};

const sourceLabel = (sourceTable) => {
  if (isEmptyValue(sourceTable)) return "";
  return `Source: ${sourceTable}`;
};

const formatLoaLabProcedure = (item, index) => {
  if (typeof item === "string") {
    return { title: item, meta: [] };
  }

  const cpt = item?.cpt || {};
  const title =
    item?.cpt_desc ||
    cpt?.cpt_desc ||
    item?.description ||
    item?.procedure_name ||
    item?.name ||
    item?.cpt_code ||
    cpt?.cpt_code ||
    item?.loa_cpt_code ||
    item?.provider_cpt_code ||
    item?.cpt_pkg_code ||
    `Procedure ${index + 1}`;
  const rate =
    item?.cpt_rate ??
    cpt?.cpt_rate ??
    item?.rate ??
    item?.loa_cpt_amount ??
    item?.loa_cpt_amt ??
    item?.amount ??
    item?.default_price;
  const meta = [
    item?.cpt_code || cpt?.cpt_code
      ? `CPT: ${item?.cpt_code || cpt?.cpt_code}`
      : "",
    item?.provider_cpt_code || cpt?.provider_cpt_code
      ? `Provider CPT: ${item?.provider_cpt_code || cpt?.provider_cpt_code}`
      : "",
    item?.cpt_pkg_code ? `Package: ${item.cpt_pkg_code}` : "",
    !isEmptyValue(rate) ? `Price: ${formatCurrency(rate)}` : "",
  ].filter(Boolean);

  return { title, meta };
};

const formatLoaIcd = (item, index) => {
  if (typeof item === "string") {
    return { title: item, meta: [] };
  }

  const icdCode = item?.icd_10_cm || item?.icd_code || `ICD ${index + 1}`;
  const condition = item?.condition;
  const title = !isEmptyValue(condition)
    ? `(${icdCode}) -- ${condition}`
    : icdCode;
  const meta = [
    item?.ref_icd_id || item?.tbl_id ? `ICD ID: ${item?.ref_icd_id || item?.tbl_id}` : "",
    item?.icd_9_cm ? `ICD 9: ${item.icd_9_cm}` : "",
    item?.categ_code || item?.categ_desc
      ? `Category: ${[item?.categ_code, item?.categ_desc].filter(Boolean).join(" - ")}`
      : "",
    item?.level_code || item?.level_desc
      ? `Level: ${[item?.level_code, item?.level_desc].filter(Boolean).join(" - ")}`
      : "",
    item?.remarks ? `Remarks: ${item.remarks}` : "",
  ].filter(Boolean);

  return { title, meta };
};

const statusBadgeClass = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("cancel") || status.includes("reject")) {
    return "border-red-200 bg-red-50 text-red-700";
  }
  if (status.includes("paid") || status.includes("processed")) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status.includes("rtp") || status.includes("return")) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (status.includes("approval") || status.includes("processing")) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
};

const Field = ({ label, value, format = display, wide = false }) => (
  <div className={wide ? "md:col-span-2 xl:col-span-3" : ""}>
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </dt>
    <dd className="mt-1 break-words text-sm font-medium text-slate-900">
      {format(value)}
    </dd>
  </div>
);

const Section = ({ id, icon, title, children }) => (
  <section id={id} className="rounded-lg border border-slate-200 bg-white">
    <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
      <FontAwesomeIcon icon={icon} className="text-indigo-600" />
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    </div>
    <dl className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
      {children}
    </dl>
  </section>
);

const SummaryCard = ({ label, value, tone = "slate", format = display }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-900",
    blue: "border-blue-200 bg-blue-50 text-blue-900",
    green: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
  };

  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-bold">{format(value)}</p>
    </div>
  );
};

const DocumentList = ({ title, items = [] }) => {
  const normalizedItems = Array.isArray(items) ? items : [];

  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </dt>
      <dd className="mt-2 space-y-2">
        {normalizedItems.length === 0 ? (
          <span className="text-sm font-medium text-slate-900">-</span>
        ) : (
          normalizedItems.map((item, index) => {
            const name =
              item.original_file_name ||
              item.file_name ||
              item.filename ||
              item.upload_id ||
              `Document ${index + 1}`;
            const url = item.signed_url || item.url;
            const meta = [
              item.label,
              item.doc_type,
              formatFileSize(item.file_size),
              item.url_expires_at ? `Expires ${formatDate(item.url_expires_at)}` : "",
            ].filter(Boolean);

            return (
              <div
                key={`${name}-${index}`}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                  >
                    {name}
                  </a>
                ) : (
                  <span className="break-all text-sm font-semibold text-slate-900">
                    {name}
                  </span>
                )}
                {meta.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {meta.join(" | ")}
                  </p>
                )}
              </div>
            );
          })
        )}
      </dd>
    </div>
  );
};

const PhysicianList = ({ title, items = [], fallbackName }) => {
  const normalizedItems = Array.isArray(items) ? items : [];
  const rows =
    normalizedItems.length > 0
      ? normalizedItems
      : !isEmptyValue(fallbackName)
        ? [{ physician_name: fallbackName }]
        : [];

  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </dt>
      <dd className="mt-2 space-y-2">
        {rows.length === 0 ? (
          <span className="text-sm font-medium text-slate-900">-</span>
        ) : (
          rows.map((item, index) => {
            const name =
              item.physician_name ||
              item.accredited_physician_name ||
              item.doctor_name ||
              `Physician ${index + 1}`;
            const meta = [
              item.physician_id ? `ID: ${item.physician_id}` : "",
              item.specialization,
              item.benefit_code ? `Benefit: ${item.benefit_code}` : "",
              !isEmptyValue(item.amount) ? `PF: ${formatCurrency(item.amount)}` : "",
              item.philhealth ? `PhilHealth: ${item.philhealth}` : "",
              item.whole_philhealth ? `Total PH: ${item.whole_philhealth}` : "",
              item.loa_no ? `LOA: ${item.loa_no}` : "",
            ].filter(Boolean);

            return (
              <div
                key={`${name}-${item.tbl_id || index}`}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <p className="break-words text-sm font-semibold text-slate-900">
                  {name}
                </p>
                {meta.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {meta.join(" | ")}
                  </p>
                )}
              </div>
            );
          })
        )}
      </dd>
    </div>
  );
};

const SourcePill = ({ sourceTable }) => {
  const label = sourceLabel(sourceTable);
  if (!label) return null;

  return (
    <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
      {label}
    </span>
  );
};

const ProcedureList = ({ title, items = [], sourceTable = "" }) => {
  const normalizedItems = Array.isArray(items) ? items : [];

  return (
    <div className="md:col-span-2 xl:col-span-3">
      <dt className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span>{title}</span>
        <SourcePill sourceTable={sourceTable} />
      </dt>
      <dd className="mt-2 space-y-2">
        {normalizedItems.length === 0 ? (
          <span className="text-sm font-medium text-slate-900">-</span>
        ) : (
          normalizedItems.map((item, index) => {
            const procedure = formatLoaLabProcedure(item, index);

            return (
              <div
                key={`${procedure.title}-${index}`}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <p className="break-words text-sm font-semibold text-slate-900">
                  {display(procedure.title)}
                </p>
                {procedure.meta.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {procedure.meta.join(" | ")}
                  </p>
                )}
              </div>
            );
          })
        )}
      </dd>
    </div>
  );
};

const IcdList = ({ title, items = [], sourceTable = "" }) => {
  const normalizedItems = Array.isArray(items) ? items : [];

  return (
    <div className="md:col-span-2 xl:col-span-3">
      <dt className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span>{title}</span>
        <SourcePill sourceTable={sourceTable} />
      </dt>
      <dd className="mt-2 space-y-2">
        {normalizedItems.length === 0 ? (
          <span className="text-sm font-medium text-slate-900">-</span>
        ) : (
          normalizedItems.map((item, index) => {
            const icd = formatLoaIcd(item, index);

            return (
              <div
                key={`${icd.title}-${index}`}
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <p className="break-words text-sm font-semibold text-slate-900">
                  {display(icd.title)}
                </p>
                {icd.meta.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {icd.meta.join(" | ")}
                  </p>
                )}
              </div>
            );
          })
        )}
      </dd>
    </div>
  );
};

const TransactionDetailsView = ({ data = {}, compact = false }) => {
  const trnStatus = data.trn_status || data.trans_flag_desc || data.loa_no;
  const claimStatus = data.claim_status || "No claim status";
  const claimDocuments = data.claim_documents || {};
  const claimPhysicians = Array.isArray(data.claim_physicians)
    ? data.claim_physicians
    : [];
  const loaPhysicians = Array.isArray(data.loa_physicians)
    ? data.loa_physicians
    : [];
  const loaCptPayload = getLoaCptPayload(data);
  const loaIcdPayload = getLoaIcdPayload(data);
  const displayLoaNo = getDisplayLoaNo(data);

  return (
    <div className={compact ? "space-y-4" : "space-y-5"}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Transaction No." value={data.trans_no} tone="blue" />
        <SummaryCard label="LOA No." value={displayLoaNo} />
        <SummaryCard
          label="LOA Amount"
          value={data.loa_amount}
          format={formatCurrency}
          tone="green"
        />
        <SummaryCard
          label="Claim Amount"
          value={data.claim_amount}
          format={formatCurrency}
          tone="amber"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusBadgeClass(trnStatus)}`}>
          TRN: {display(trnStatus)}
        </span>
        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusBadgeClass(claimStatus)}`}>
          Claim: {display(claimStatus)}
        </span>
        {Number(data.rtp_tag || 0) === 1 && (
          <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            RTP
          </span>
        )}
        {Number(data.stale_tag || 0) === 1 && (
          <span className="inline-flex rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
            Stale
          </span>
        )}
      </div>

      <Section id="transaction" icon={faFile} title="Transaction Information">
        <Field label="Transaction Date" value={data.trans_date} format={formatDate} />
        <Field label="Admission Date" value={data.admission_date} format={formatDateOnly} />
        <Field label="Generated By" value={data.generated_by} />
        <Field label="TRN Flag Code" value={data.trans_flag_status} />
        <Field label="TRN Status" value={trnStatus} />
        <Field label="LOA Issued Date" value={data.loa_date_issued} format={formatDate} />
        <Field label="Valid Until" value={data.expire_date || data.expiry_date} format={formatDateOnly} />
        <Field label="Form Number" value={data.form_number} />
        <Field label="Bill Number" value={data.bill_no} />
      </Section>

      <Section id="member" icon={faUserCircle} title="Member and Corporate">
        <Field label="Member ID" value={data.member_id} />
        <Field label="Member Name" value={data.member_name} />
        <Field label="Gender" value={data.gender} />
        <Field label="Date of Birth" value={data.date_of_birth} format={formatDateOnly} />
        <Field label="Civil Status" value={data.civil_status} />
        <Field label="Plan Code" value={data.plan_code} />
        <Field label="Plan Name" value={data.plan_name} />
        <Field label="Member Expiry" value={data.expiry_date} format={formatDateOnly} />
        <Field label="Corporate" value={`${display(data.corp_name)} (${display(data.corp_code)})`} />
        <Field label="Special Instructions" value={data.special_instructions} wide />
      </Section>

      <Section id="loa" icon={faIdCard} title="LOA, Coverage, and Provider">
        <Field label="Coverage Code" value={data.coverage_code} />
        <Field label="Coverage Description" value={data.coverage_desc} />
        <Field label="LOA Amount" value={data.loa_amount} format={formatCurrency} />
        <Field label="Provider" value={`${display(data.provider_name)} (${display(data.provider_code)})`} />
        <Field label="Payment Terms" value={data.payment_terms} />
        <Field label="Hospitalization Account" value={data.hospitalization_account} />
        <Field label="ER Fee" value={data.er_fee} format={formatCurrency} />
        <Field label="Professional Fee" value={data.prof_fee} format={formatCurrency} />
        <Field label="Physician" value={data.physician_name || data.doctor_name} />
      </Section>

      <Section id="physicians" icon={faStethoscope} title="Doctors / Physicians">
        <div className="space-y-4 md:col-span-2 xl:col-span-3">
          <PhysicianList
            title="Claims Process Doctors"
            items={claimPhysicians}
            fallbackName={data.doctor_name}
          />
          <PhysicianList
            title="LOA Physicians"
            items={loaPhysicians}
            fallbackName={data.physician_name}
          />
        </div>
      </Section>

      <Section id="claims" icon={faClipboardList} title="Claims Process">
        <Field label="Claim Status" value={data.claim_status} />
        <Field label="Claim Amount" value={data.claim_amount} format={formatCurrency} />
        <Field label="Claim Processor" value={data.claim_processor} />
        <Field label="Claim Processed Date" value={data.claim_processed_date} format={formatDate} />
        <Field label="Claim Remarks" value={data.claim_remarks} wide />
        <Field label="RTP Tag" value={Number(data.rtp_tag || 0) === 1 ? "Yes" : "No"} />
        <Field label="Stale Tag" value={Number(data.stale_tag || 0) === 1 ? "Yes" : "No"} />
        <Field label="Latest RTP Code" value={data.rtp_code} />
        <Field label="Latest RTP Remarks" value={data.rtp_remarks} wide />
        <Field label="RTP Cancel Status" value={data.rtp_cancel_status} />
        <Field label="RTP Cancel Request ID" value={data.rtp_cancel_request_id} />
        <Field label="HMC VAT Tag" value={data.hmc_vat_tag} />
        <Field label="Transmittal" value={data.transmittal} />
      </Section>

      <Section id="batch" icon={faHashtag} title="Batch and SOA">
        <Field label="Batch No." value={data.batch_no} />
        <Field label="SOA No." value={data.soa_no} />
        <Field label="Batch Type" value={data.batch_type} />
        <Field label="Batch Status" value={data.batch_status} />
        <Field label="Received Date" value={data.batch_received_date} format={formatDateOnly} />
        <Field label="Due Date" value={data.batch_due_date} format={formatDateOnly} />
        <Field label="Estimated Claims" value={data.est_claim_count} />
        <Field label="Estimated Amount" value={data.est_claim_amount} format={formatCurrency} />
        <Field label="Actual Claims" value={data.act_claim_count} />
        <Field label="Actual Amount" value={data.act_claim_amount} format={formatCurrency} />
        <Field label="Created By" value={data.batch_created_by} />
        <Field label="Created Date" value={data.batch_created_date} format={formatDate} />
        <Field label="Submitted By" value={data.batch_submitted_by} />
        <Field label="Submitted Date" value={data.batch_submitted_date} format={formatDate} />
        <Field label="Accepted By" value={data.batch_accepted_by} />
        <Field label="SOA File Upload" value={data.soa_fileupload} wide />
        <Field label="Batch Remarks" value={data.batch_remarks} wide />
      </Section>

      <Section id="diagnosis" icon={faStethoscope} title="Diagnosis, ICD, and Procedure">
        <Field label="Diagnosis" value={data.diagnosis} wide />
        <Field label="Chief Complaint" value={data.chief_complaint} wide />
        <Field label="Requested Procedure" value={data.requested_lab_procedure} wide />
        <ProcedureList
          title="LOA CPT / Lab Procedure(s)"
          items={loaCptPayload.rows}
          sourceTable={loaCptPayload.sourceTable}
        />
        <IcdList
          title="LOA ICD"
          items={loaIcdPayload.rows}
          sourceTable={loaIcdPayload.sourceTable}
        />
        <Field label="Procedure Date" value={data.proc_date} format={formatDateOnly} />
        <Field label="Doctor Name" value={data.doctor_name} />
        <Field label="ICD ID" value={data.icd_id} />
        <Field label="ICD 10 CM" value={data.icd_10_cm} />
        <Field label="Condition" value={data.condition} wide />
      </Section>

      <Section id="remarks" icon={faComment} title="Remarks and Documents">
        <Field label="Remarks" value={data.remarks} wide />
        <Field label="Approver Remarks" value={data.approver_remarks} wide />
        <Field label="Cancelled Details" value={data.cancelled_details} wide />
        <Field label="LOA Document" value={data.loa_document} />
        <Field label="Discharge SOA" value={data.discharge_soa} />
        <Field label="Package Name" value={data.package_name} />
        <div className="space-y-4 md:col-span-2 xl:col-span-3">
          <DocumentList
            title="Claim Attachments"
            items={claimDocuments.attachments}
          />
          <DocumentList title="SOA Files" items={claimDocuments.soa_files} />
          <DocumentList
            title="Legacy / Other File References"
            items={claimDocuments.other_files_reference}
          />
        </div>
      </Section>
    </div>
  );
};

export default TransactionDetailsView;
